import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import {
  PaymentMethodSchema,
  SchemaValidationFailedError,
  TicketStatusSchema,
  TicketSubStageSchema,
} from '@kilat/shared';

/**
 * Typed wrappers around queue-related Cloud Functions.
 * Feature code uses these; never imports firebase/functions directly.
 */

// ── advanceTicket ────────────────────────────────────────────────────────

export const AdvanceTicketInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  ticketId: z.string().min(1),
  to: TicketStatusSchema,
  // Roadmap #13 slice B — optional wash-bay assignment. Sent only on the
  // Start-Next (inQueue → inWashingProcess) path when the outlet has ≥1 active
  // bay; the server validates it exists + is active and writes `ticket.bayId`.
  bayId: z.string().min(1).optional(),
});
export type AdvanceTicketInput = z.infer<typeof AdvanceTicketInputSchema>;

export const AdvanceTicketOutputSchema = z.object({
  from: TicketStatusSchema,
  to: TicketStatusSchema,
});
export type AdvanceTicketOutput = z.infer<typeof AdvanceTicketOutputSchema>;

const advanceTicketFn: HttpsCallable<AdvanceTicketInput, AdvanceTicketOutput> = httpsCallable(
  functions,
  'advanceTicket',
);

export async function callAdvanceTicket(input: AdvanceTicketInput): Promise<AdvanceTicketOutput> {
  const parsed = AdvanceTicketInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('advanceTicket.input', parsed.error.flatten());
  }
  const result = await advanceTicketFn(parsed.data);
  const out = AdvanceTicketOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('advanceTicket.output', out.error.flatten());
  }
  return out.data;
}

// ── recordPayment ────────────────────────────────────────────────────────

export const RecordPaymentInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  ticketId: z.string().min(1),
  method: PaymentMethodSchema,
  amount: z.number().int().nonnegative(),
  voucherIds: z.array(z.string()).default([]),
  promotionIds: z.array(z.string()).default([]),
  notes: z.string().max(500).optional(),
});
export type RecordPaymentInput = z.infer<typeof RecordPaymentInputSchema>;

export const RecordPaymentOutputSchema = z.object({
  from: TicketStatusSchema,
  to: TicketStatusSchema,
  pricing: z
    .object({
      subtotal: z.number().int().nonnegative(),
      discountTotal: z.number().int().nonnegative(),
      systemTotal: z.number().int().nonnegative(),
    })
    .optional(),
});
export type RecordPaymentOutput = z.infer<typeof RecordPaymentOutputSchema>;

const recordPaymentFn: HttpsCallable<RecordPaymentInput, RecordPaymentOutput> = httpsCallable(
  functions,
  'recordPayment',
);

export async function callRecordPayment(input: RecordPaymentInput): Promise<RecordPaymentOutput> {
  const parsed = RecordPaymentInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('recordPayment.input', parsed.error.flatten());
  }
  const result = await recordPaymentFn(parsed.data);
  const out = RecordPaymentOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('recordPayment.output', out.error.flatten());
  }
  return out.data;
}

// ── appendItemsToTicket (Sprint 119 — pre-payment upsell) ────────────────

export const AppendItemsInputSchema = z
  .object({
    tenantId: z.string().min(1),
    outletId: z.string().min(1),
    ticketId: z.string().min(1),
    addOnIds: z.array(z.string().min(1)).max(10).default([]),
    // Product cross-sell lines (Pro) — server prices + gates them. Empty by
    // default so the existing add-on-only POS callers are unaffected.
    productSelections: z
      .array(z.object({ productId: z.string().min(1), qty: z.number().int().positive().max(99) }))
      .max(10)
      .default([]),
  })
  .refine((v) => v.addOnIds.length > 0 || v.productSelections.length > 0, {
    message: 'Provide at least one add-on or product to append.',
  });
// `z.input` (pre-default) so existing callers may omit `productSelections`
// (and `addOnIds`); `callAppendItemsToTicket` parses to fill the defaults.
export type AppendItemsInput = z.input<typeof AppendItemsInputSchema>;

export const AppendItemsOutputSchema = z.object({
  addedItems: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      price: z.number().int().nonnegative(),
    }),
  ),
  addedItemsTotal: z.number().int().nonnegative(),
  // Products appended in this amendment (empty for an add-on-only append).
  addedProducts: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        priceSen: z.number().int().nonnegative(),
        qty: z.number().int().positive(),
        lineTotalSen: z.number().int().nonnegative(),
      }),
    )
    .default([]),
  addedProductsTotal: z.number().int().nonnegative().default(0),
  newSubtotalSen: z.number().int().nonnegative(),
  newTotalSen: z.number().int().nonnegative(),
  priorTotalSen: z.number().int().nonnegative(),
});
export type AppendItemsOutput = z.infer<typeof AppendItemsOutputSchema>;

const appendItemsFn: HttpsCallable<AppendItemsInput, AppendItemsOutput> = httpsCallable(
  functions,
  'appendItemsToTicket',
);

export async function callAppendItemsToTicket(input: AppendItemsInput): Promise<AppendItemsOutput> {
  const parsed = AppendItemsInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('appendItemsToTicket.input', parsed.error.flatten());
  }
  const result = await appendItemsFn(parsed.data);
  const out = AppendItemsOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('appendItemsToTicket.output', out.error.flatten());
  }
  return out.data;
}

// ── reorderQueue ─────────────────────────────────────────────────────────

export const ReorderQueueInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  ticketId: z.string().min(1),
  toPosition: z.number().int().min(1),
});
export type ReorderQueueInput = z.infer<typeof ReorderQueueInputSchema>;

export const ReorderQueueOutputSchema = z.object({
  changed: z.number().int().nonnegative(),
  from: z.number().int().positive(),
  to: z.number().int().positive(),
});
export type ReorderQueueOutput = z.infer<typeof ReorderQueueOutputSchema>;

const reorderQueueFn: HttpsCallable<ReorderQueueInput, ReorderQueueOutput> = httpsCallable(
  functions,
  'reorderQueue',
);

export async function callReorderQueue(input: ReorderQueueInput): Promise<ReorderQueueOutput> {
  const parsed = ReorderQueueInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('reorderQueue.input', parsed.error.flatten());
  }
  const result = await reorderQueueFn(parsed.data);
  const out = ReorderQueueOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('reorderQueue.output', out.error.flatten());
  }
  return out.data;
}

// ── setSubStage (roadmap #13 slice A — wash sub-stages) ───────────────────

export const SetSubStageInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  ticketId: z.string().min(1),
  // null clears the sub-stage; a value sets it. `.nullable()` mirrors the
  // server InputSchema so an explicit clear round-trips cleanly.
  subStage: TicketSubStageSchema.nullable(),
});
export type SetSubStageInput = z.infer<typeof SetSubStageInputSchema>;

export const SetSubStageOutputSchema = z.object({
  subStage: TicketSubStageSchema.nullable(),
});
export type SetSubStageOutput = z.infer<typeof SetSubStageOutputSchema>;

const setSubStageFn: HttpsCallable<SetSubStageInput, SetSubStageOutput> = httpsCallable(
  functions,
  'setSubStage',
);

export async function callSetSubStage(input: SetSubStageInput): Promise<SetSubStageOutput> {
  const parsed = SetSubStageInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('setSubStage.input', parsed.error.flatten());
  }
  const result = await setSubStageFn(parsed.data);
  const out = SetSubStageOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('setSubStage.output', out.error.flatten());
  }
  return out.data;
}
